import type { ProjectDetail as Project, TurnOptions } from "@esi/shared";
import { FileUp, ListFilter, Lock, PanelRight, Upload, X } from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useMediaQuery } from "@/hooks/use-media-query";
import { ArtefactLayout } from "../conversation/artefacts/ArtefactLayout.tsx";
import { AttachmentWorkspace } from "../conversation/attachments/AttachmentWorkspace.tsx";
import { ProjectLink } from "../conversation/ConversationHeader.tsx";
import { ConversationView } from "../conversation/ConversationView.tsx";
import { useRefreshConversations } from "../conversation/queries.ts";
import { StartConversation } from "../conversation/Thread.tsx";
import { handedOffOptions, isQuestionHandoff } from "../home/handoff.ts";
import { lastProject } from "../home/last-project.ts";
import {
  Link,
  navigate,
  PROJECTS_PATH,
  projectConversationPath,
  projectHomePath,
  useOpenArtefact,
  useOpenWiki,
  useSearch,
} from "../router.tsx";
import { useSession } from "../session.tsx";
import { KnowledgePanel } from "./KnowledgePanel.tsx";
import { KnowledgeTree, PAGE_SCROLL, useFolderParam, useUploadDialog } from "./KnowledgeTree.tsx";
import { useKnowledgePanel } from "./knowledge-panel-state.ts";
import { useDocuments, useProject } from "./queries.ts";
import { shortId } from "./readers.ts";
import { acceptsStatus, sourceStatusFilter } from "./source-status.ts";

/** Loads a project, then renders `children` with it (a skeleton, or a refusal with a way back). */
export function WithProject(props: {
  projectId: string;
  children: (project: Project) => ReactNode;
}) {
  const { t } = useTranslation();
  const project = useProject(props.projectId);
  if (project.isPending) {
    return (
      <div className="flex flex-col gap-3 px-6 py-5" aria-busy="true">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-72" />
      </div>
    );
  }
  // A failed refetch keeps what is shown (and any unsaved edit in it): only a first load fails.
  if (project.isError && !project.data) {
    return (
      <Page width="narrow">
        <Notice
          tone="danger"
          action={
            <Button asChild size="sm" variant="outline">
              <Link href={PROJECTS_PATH}>{t("project.backToProjects")}</Link>
            </Button>
          }
        >
          {project.error.message}
        </Notice>
      </Page>
    );
  }
  return <>{props.children(project.data)}</>;
}

/** Owner, visibility and the viewer's role, as one quiet line. */
export function useProjectMeta(project: Project): string {
  const { t } = useTranslation();
  return [
    project.owner.kind === "user"
      ? t("project.personal")
      : t("project.entityOwned", {
          entity: project.ownerName ?? shortId(project.owner.entityId),
        }),
    t(`project.visibility.${project.visibility}`),
    t("project.yourRole", { role: t(`projectRoles.${project.role}.name`) }),
  ].join(" · ");
}

/** The read-only marker of a frozen project, as words (never colour alone). */
export function FrozenMark() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 text-xs text-fg-secondary">
      <Lock aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      {t("project.frozenShort")}
    </span>
  );
}

/**
 * A project conversation at its own URL (spec `web-shell` "Project workspace"): the conversation
 * beside its knowledge panel (a sheet on narrow screens), the project named once, in the
 * conversation's header. `conversationId` "new" starts one.
 */
export function ProjectView({
  projectId,
  conversationId,
}: {
  projectId: string;
  conversationId: string;
}) {
  return (
    <WithProject projectId={projectId}>
      {(project) => (
        <AttachmentWorkspace conversationId={conversationId === "new" ? null : conversationId}>
          <ProjectWorkspace project={project} conversationId={conversationId} />
        </AttachmentWorkspace>
      )}
    </WithProject>
  );
}

function ProjectWorkspace({
  project,
  conversationId,
}: {
  project: Project;
  conversationId: string;
}) {
  const { t } = useTranslation();
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const knowledgeButton = useRef<HTMLButtonElement>(null);
  const pendingArtefact = useRef<{ artefactId: string; version: number } | null>(null);
  const { open: artefact, show: showArtefact, close: closeArtefact } = useOpenArtefact();
  const { open: wikiPanel, close: closeWiki } = useOpenWiki();
  // One knowledge panel at a time: beside the conversation when wide (unless the user hides
  // it), else in a sheet.
  const wide = useMediaQuery("(min-width: 1024px)");
  const docked = useKnowledgePanel();
  const panelId = useId();
  useEffect(() => {
    lastProject.write(project.id);
  }, [project.id]);
  useDocumentTitle(project.name, conversationId === "new");
  const conversationScope = useMemo(
    () => (conversationId === "new" ? null : { projectId: project.id, conversationId }),
    [project.id, conversationId],
  );
  // One control at every width, in the same place: wide, it hides and shows the docked panel;
  // narrow, it opens the sheet.
  // An open artefact holds the column: the panel is not shown, and pressing brings it back.
  // The wiki panel holds the column the same way (wiki-collaboration D8a).
  const artefactOpen = conversationId !== "new" && (artefact !== null || wikiPanel !== null);
  const knowledgeShown = docked.shown && !artefactOpen;
  const knowledgeTrigger = (
    <Button
      ref={knowledgeButton}
      variant="ghost"
      size="sm"
      {...(wide
        ? {
            "aria-pressed": knowledgeShown,
            "aria-controls": panelId,
            onClick: () => {
              if (artefactOpen) {
                if (artefact) {
                  closeArtefact();
                } else {
                  closeWiki();
                }
              }
              docked.set(!knowledgeShown);
            },
          }
        : { "aria-haspopup": "dialog" as const, onClick: () => setKnowledgeOpen(true) })}
    >
      <PanelRight aria-hidden="true" strokeWidth={1.75} />
      {t("project.openKnowledge")}
    </Button>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {project.frozenAt ? (
        <div className="flex items-start gap-1.5 border-b border-line px-4 py-2 sm:px-6">
          <Lock
            aria-hidden="true"
            className="mt-0.5 size-3.5 shrink-0 text-fg-secondary"
            strokeWidth={1.75}
          />
          <p className="text-sm text-fg-secondary">{t("project.frozen")}</p>
        </div>
      ) : null}

      <ArtefactLayout
        conversationLabel={t("project.conversation")}
        scope={conversationScope}
        knowledge={{
          label: t("project.knowledge"),
          shown: docked.shown,
          id: panelId,
          panel: (
            <KnowledgePanel project={project} narrow={false} conversation={conversationScope} />
          ),
        }}
        conversation={
          conversationId === "new" ? (
            <NewConversation project={project} extra={knowledgeTrigger} />
          ) : (
            <ConversationView
              conversationId={conversationId}
              project={project}
              headerExtra={knowledgeTrigger}
            />
          )
        }
      />

      <Sheet open={!wide && knowledgeOpen} onOpenChange={setKnowledgeOpen}>
        <SheetContent
          side="right"
          className="w-full gap-0 p-0 sm:max-w-md"
          onCloseAutoFocus={(event) => {
            const pending = pendingArtefact.current;
            pendingArtefact.current = null;
            if (pending) {
              event.preventDefault();
              knowledgeButton.current?.focus();
              showArtefact(pending);
            }
          }}
          // Focus the sheet itself, not the title filter: on a phone that would open the keyboard.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            (event.currentTarget as HTMLElement).focus();
          }}
        >
          <SheetHeader className="border-b border-line">
            <SheetTitle>{t("project.knowledge")}</SheetTitle>
            <SheetDescription className="sr-only">{project.name}</SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col">
            <KnowledgePanel
              project={project}
              narrow
              conversation={conversationScope}
              onOpenArtefact={(artefactId, version) => {
                pendingArtefact.current = { artefactId, version };
                setKnowledgeOpen(false);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/**
 * A question handed over from home, read from the history entry once (then cleared). `consume`
 * drops it once asked, so a later new conversation never asks it again.
 */
function useHandoff(): [string | null, () => void, TurnOptions | undefined] {
  // Pure read: StrictMode may run the initializer twice.
  const [question, setQuestion] = useState(() =>
    isQuestionHandoff(window.history.state) ? window.history.state.question : null,
  );
  const [options] = useState(() =>
    isQuestionHandoff(window.history.state) ? handedOffOptions(window.history.state) : undefined,
  );
  useEffect(() => {
    if (question !== null) {
      // A reload or coming back to this entry must never ask it again.
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, [question]);
  return [question, () => setQuestion(null), options];
}

/** "New conversation" in a project: sending creates it, then it opens at its own URL. */
function NewConversation({ project, extra }: { project: Project; extra: ReactNode }) {
  const { t } = useTranslation();
  const [handoff, consumeHandoff, handoffOptions] = useHandoff();
  const refresh = useRefreshConversations();
  const bar = (
    <div className="flex h-bar shrink-0 items-center justify-between gap-2 border-b border-line px-3 sm:px-4">
      {/* The project, once, as in a conversation's header; the page's heading after it. */}
      <div className="flex min-w-0 items-center gap-2 text-xs text-fg-muted">
        <ProjectLink project={project} />
        <span aria-hidden="true">/</span>
        <h1 className="shrink-0 text-sm font-semibold text-fg">
          {t("projectHome.newConversation")}
        </h1>
      </div>
      {extra}
    </div>
  );
  if (project.frozenAt) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {bar}
        <p className="px-4 py-6 text-sm text-fg-secondary">{t("conversation.frozen")}</p>
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {bar}
      <StartConversation
        projectId={project.id}
        residency={project.residency?.value}
        initialQuestion={handoff}
        initialOptions={handoffOptions}
        onAsked={consumeHandoff}
        onTurnEnd={(id) => refresh(id)}
        onStarted={(id) => {
          refresh();
          navigate(projectConversationPath(project.id, id), { replace: true });
        }}
      />
    </div>
  );
}

/**
 * The project home's Files tab: the knowledge tree scoped to Files at full width (design §11),
 * with the table details as columns on wide screens. The opened folder is `?folder=`.
 */
export function Files({ project }: { project: Project }) {
  const { t } = useTranslation();
  const documents = useDocuments(project.id);
  const upload = useUploadDialog(project);
  const wide = useMediaQuery("(min-width: 768px)");
  // Columns need room beside the rail: below this the rows stay stacked (as the old table did).
  const columns = useMediaQuery("(min-width: 1280px)");
  const [folder, setFolder] = useFolderParam();
  const search = useSearch();
  const linked = search.get("document")?.toLowerCase();
  // `?status=` (from Overview's Sources figures): the tree narrowed to failed or processing files.
  const status = sourceStatusFilter(search.get("status"));
  const { userId } = useSession();
  const mine = status !== null && search.get("uploader") === "me";
  const only = useMemo(
    () => (status ? acceptsStatus(status, mine ? userId : null) : null),
    [status, mine, userId],
  );
  const clearStatus = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("status");
    params.delete("uploader");
    const query = params.toString();
    navigate(`${window.location.pathname}${query ? `?${query}` : ""}`, { replace: true });
  };
  // Narrow screens: where the tree is drilled (from the URL or the remembered root).
  const [root, setRoot] = useState<string | null>(null);

  // Bounded and centred at `Page`'s default `max-w-5xl`: about the room the columns get when they
  // appear (1280px less the sidebar), so wider screens do not spread them apart.
  return (
    <section
      data-esi-content="files"
      aria-labelledby="files-heading"
      className="@container mx-auto flex w-full max-w-5xl flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="files-heading" className="text-sm font-semibold text-fg">
          {t("files.title")}
          {documents.data ? (
            <span className="ml-1.5 font-normal text-fg-muted tabular-nums">
              {documents.data.documents.length}
            </span>
          ) : null}
        </h2>
        {upload.upload ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={!upload.ready}
            // Narrow screens: into the folder opened as the root; wide ones: the top level.
            onClick={() => upload.upload?.open(wide ? null : root)}
          >
            <Upload aria-hidden="true" strokeWidth={1.75} />
            {t("files.upload")}
          </Button>
        ) : null}
      </div>
      {status ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p role="status" className="inline-flex items-center gap-1.5 text-sm text-fg-secondary">
            <ListFilter aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.75} />
            {mine ? t(`files.statusFilter.mine.${status}`) : t(`files.statusFilter.${status}`)}
          </p>
          <Button size="sm" variant="ghost" className="-ml-2" onClick={clearStatus}>
            <X aria-hidden="true" strokeWidth={1.75} />
            {t("files.statusFilter.clear")}
          </Button>
        </div>
      ) : null}
      <div className="-mx-4 flex flex-col">
        <KnowledgeTree
          project={project}
          scope="files"
          only={only}
          onClearOnly={clearStatus}
          onlyEmpty={
            status ? (
              <p className="text-sm text-fg-secondary">{t(`files.statusFilter.none.${status}`)}</p>
            ) : undefined
          }
          narrow={!wide}
          columns={columns}
          folderParam={folder}
          onFolderParam={setFolder}
          linkedId={linked ? `d:${linked}` : null}
          scrollRef={PAGE_SCROLL}
          upload={upload.upload}
          onRootChange={setRoot}
          empty={
            <EmptyState icon={FileUp} title={t("files.title")}>
              {t("files.empty")}
            </EmptyState>
          }
        />
      </div>
      {upload.dialog}
    </section>
  );
}
